import { Button, Popover, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import Icon from "../../assets/Icon";
import InterviewTooltipModal from "./modals/InterviewTooltipModal";
import { Mic, MicOff } from "lucide-react";
import InactivityDisqualifyModal from "./modals/InactivityDisqualifyModal";
import WebUtils from "../utils/WebUtils";
import { addUserAnsweringTimeApi, addUserThinkingTimeApi } from "../../api";
import "../../styles/twoWayInterview.css";
// import Lottie from "lottie-react";
// import soundWaveAnimation from "../../assets/lotties/sound-wave.json";

function TwoWayMain({
  socket,
  deepgramStatus,
  audioContextRef,
  audioQueueRef,
  microphoneGainNodeRef,
  setDeepgramAudioRecorder,
  deepgramAudioRecorder,
  interviewData,
  chatMessages,
  setChatMessages,
  conversationHistory,
  currentAudioStream,
  interviewMode,
  setInterviewMode,
  ip,
  setLoading,
  videoBlobQueueRef,
  screenShareBlobQueueRef,
  timeRemainingRef,
  stopRecording,
  chatMessagesRef,
  allowUnloadRef
}) {
  const chatRef = useRef(null);
  const [vadInstance, setVadInstance] = useState(null);
  const isMayaSpeakingRef = useRef(false);
  const userSpeakingRef = useRef(false);
  const preventVadRef = useRef(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [tooltipModal, setTooltipModal] = useState(false);
  const [startSpeakingPopover, setStartSpeakingPopover] = useState(false);
  const [intermediateTranscript, setIntermediateTranscript] = useState("");

  const userThinkingStartRef = useRef(null);
  const userAnsweringStartRef = useRef(null);
  const userIntermediateTranscriptRef = useRef("");

  // const INACTIVITY_DURATION = 60 * 1000; // 3 minutes
  const INACTIVITY_DURATION = 3 * 60 * 1000; // 3 minutes

  const inactivityTimerRef = useRef(null);

  const [voiceInactiveWarningModal, setVoiceInactiveWarningModal] =
    useState(false);

  function handleDataAvailable(event) {
    if (event.data.size > 0 && socket && socket.connected) {
      socket.emit("TranscribeAudio", event.data);
    }
  }
  function sendAudioForDeepgram() {
    const mediaRecorder = new MediaRecorder(currentAudioStream);
    mediaRecorder.ondataavailable = handleDataAvailable;

    mediaRecorder.start(100);
    setDeepgramAudioRecorder(mediaRecorder);
    // console.log("Audio recorder for deepgram started.");
  }

  useEffect(() => {
    if (deepgramStatus.TrancriptionSocketStatus) {
      if (deepgramAudioRecorder) {
        deepgramAudioRecorder.stop();
        // console.log("Audio recorder stopped");
      }
      sendAudioForDeepgram();
    }
  }, [deepgramStatus]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const appendMessage = (role, content) => {
    setChatMessages((prevMessages) => [...prevMessages, { role, content }]);
  };

  async function playAudioBuffer() {
    try {
      if (!isMayaSpeakingRef.current && audioQueueRef.current.length >= 1) {
        isMayaSpeakingRef.current = true;

        const arrayBuffer = audioQueueRef.current.shift();
        let audioBuffer =
          await audioContextRef.current.decodeAudioData(arrayBuffer);
        audioBuffer = applyFadeInOut(audioBuffer);
        let source = null;

        source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(microphoneGainNodeRef.current);
        source.connect(audioContextRef.current.destination);

        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }
        source.start();

        source.onended = () => {
          isMayaSpeakingRef.current = false;
          playAudioBuffer(); // Play the next audio in the queue
        };
      }
      if (!isMayaSpeakingRef.current && audioQueueRef.current.length === 0) {
        startUserThinkingTimer();
      }
    } catch (error) {
      console.error("Error playing audio buffer:", error);
    }
  }

  const startUserThinkingTimer = () => {
    userThinkingStartRef.current = Date.now();
  };
  const startUserAnsweringTimer = () => {
    userAnsweringStartRef.current = Date.now();
  };

  const endUserAnsweringTimer = (realTranscript) => {
    if (userAnsweringStartRef.current) {
      const diff = (Date.now() - userAnsweringStartRef.current) / 1000;
      const elapsedTime =
        conversationHistory?.interviewDuration - timeRemainingRef.current;

      callUserAnsweringTimeApi(
        Number(diff.toFixed(2)),
        elapsedTime,
        realTranscript,
        // userIntermediateTranscriptRef.current,
      );
      userAnsweringStartRef.current = null;
      userIntermediateTranscriptRef.current = "";
    }
  };

  const endUserThinkingTimer = () => {
    if (userThinkingStartRef.current) {
      const diff = (Date.now() - userThinkingStartRef.current) / 1000;
      const elapsedTime =
        conversationHistory?.interviewDuration - timeRemainingRef.current;

      callUserThinkingTimeApi(Number(diff.toFixed(2)), elapsedTime);
      userThinkingStartRef.current = null;
    }
  };

  const callUserAnsweringTimeApi = (
    userAnsweringTime = 0,
    elapsedTime = 0,
    transcript = "",
  ) => {
    // console.log("User transcript", transcript);
    // console.log("answering time", userAnsweringTime);

    const wordCount = transcript.trim().split(/\s+/).length;
    const charCountNoSpaces = transcript.replace(/\s/g, "").length;

    // console.log("Word count: ", wordCount);
    // console.log("Chars count: ", charCountNoSpaces);

    const url =
      addUserAnsweringTimeApi + `?interviewId=${interviewData?.interviewId}`;

    const requestBody = {
      answeringTime: userAnsweringTime,
      elapsedTime,
      totalWordCount: wordCount,
      totalCharacterCount: charCountNoSpaces,
    };

    WebUtils.httpOperations(url, requestBody, "POST")
      .then((result) => {
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const callUserThinkingTimeApi = (userThinkingTime = 0, elapsedTime = 0) => {
    const url =
      addUserThinkingTimeApi + `?interviewId=${interviewData?.interviewId}`;

    const requestBody = {
      userThinkingTime,
      elapsedTime,
    };

    WebUtils.httpOperations(url, requestBody, "POST")
      .then((result) => {
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  function applyFadeInOut(audioBuffer) {
    const channelData = audioBuffer.getChannelData(0); // Assuming mono audio or first channel
    const fadeSamples = Math.min(30, channelData.length / 2);

    // Apply fade-in
    for (let i = 0; i < fadeSamples; i++) {
      channelData[i] *= i / fadeSamples;
    }

    // Apply fade-out
    for (let i = 0; i < fadeSamples; i++) {
      channelData[channelData.length - i - 1] *= i / fadeSamples;
    }

    return audioBuffer;
  }

  useEffect(() => {
    if (socket && socket.connected) {
      socket.on("AppendUserTranscriptUnits", (unitData) => {
        appendMessage("You", unitData);
        endUserAnsweringTimer(unitData);
      });

      socket.on("AppendBotTranscriptUnits", (unitData) => {
        appendMessage("Maya", unitData);
      });

      socket.on("IntermediateTranscript", (msg) => {
        if (userSpeakingRef.current) {
          setIntermediateTranscript(msg);
          userIntermediateTranscriptRef.current += msg + " ";
        }
      });

      socket.on("playAudio", (audioData) => {
        // audioData = applyFadeInOut(audioData);
        audioQueueRef.current.push(audioData);
        if (!isMayaSpeakingRef.current) {
          playAudioBuffer();
        }
      });
    }

    return () => {
      if (socket) {
        socket.off("AppendUserTranscriptUnits");
        socket.off("AppendBotTranscriptUnits");
        socket.off("playAudio");
      }
    };
  }, [socket]);

  useEffect(() => {
    if (interviewMode === "Manual") {
      preventVadRef.current = true;
    } else {
      preventVadRef.current = false;
    }
  }, [interviewMode]);

  // Voice Inactivity tracker
  useEffect(() => {
    if (isUserSpeaking) {
      // Clear inactive timer when user speaks
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    inactivityTimerRef.current = setTimeout(() => {
      setVoiceInactiveWarningModal(true);
    }, INACTIVITY_DURATION);

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [isUserSpeaking]);

  const resumeInterviewHandler = () => {
    setVoiceInactiveWarningModal(false);

    // Reset the inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      setVoiceInactiveWarningModal(true);
    }, INACTIVITY_DURATION);
  };

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    const loadVAD = async () => {
      try {
        await loadScript(
          "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.19.2/dist/ort.min.js",
        );
        await loadScript(
          "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.19/dist/bundle.min.js",
        );

        // Initialize the VAD after scripts are loaded
        let pauseFrames = 12; //16
        if (conversationHistory?.speechEndTimer === "Long") {
          pauseFrames = 20;
        } else if (conversationHistory?.speechEndTimer === "Short") {
          pauseFrames = 8; //12
        }
        const myvad = await window.vad.MicVAD.new({
          sampleRate: 48000,
          frameSamples: 1800,
          redemptionFrames: pauseFrames, //no. of consecutive silent frames required to detect the end of speech.
          positiveSpeechThreshold: 0.9, //higher the value means frames with very strong speech signals are classified as speech. (needs to be high)
          negativeSpeechThreshold: 0.2,

          onSpeechStart: () => {
            console.log("Speech started.");
            if (preventVadRef.current) {
              setStartSpeakingPopover(true);
            } else {
              audioQueueRef.current = [];
              setIsUserSpeaking(true);
              userSpeakingRef.current = true;
              endUserThinkingTimer();
              startUserAnsweringTimer();
              socket.emit("SpeechStarted", "HANDS-FREE");
            }
          },
          onSpeechEnd: (audio) => {
            console.log("Speech ended.");
            if (preventVadRef.current) {
              setTimeout(() => {
                setStartSpeakingPopover(false);
              }, 3000);
            } else {
              setIsUserSpeaking(false);
              userSpeakingRef.current = false;
              // endUserAnsweringTimer();
              socket.emit("SpeechEnded");
              setIntermediateTranscript("");
            }
          },
        });

        setVadInstance(myvad);
      } catch (error) {
        console.error("Error loading scripts or initializing VAD:", error);
      }
    };
    loadVAD();
  }, []);

  useEffect(() => {
    if (vadInstance) {
      vadInstance.start();
    }
  }, [vadInstance]);
  // useEffect(() => {
  //   if (vadInstance && interviewMode !== "Manual") {
  //     vadInstance.start();
  //   } else if (vadInstance && interviewMode === "Manual") {
  //     vadInstance.pause();
  //   }
  // }, [vadInstance, interviewMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "Space") {
        event.preventDefault(); // Prevent default spacebar scrolling
        if (!isUserSpeaking) {
          startSpeechManual();
        } else {
          stopSpeechManual();
        }
      }
    };

    if (interviewMode === "Manual") {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [interviewMode, isUserSpeaking]);

  // useEffect(() => {
  //   setTooltipModal(true)
  // }, [interviewMode])

  const startSpeechManual = () => {
    setIsUserSpeaking(true);
    endUserThinkingTimer();
    startUserAnsweringTimer();

    userSpeakingRef.current = true;
    console.log("Speech started manually.");
    audioQueueRef.current = [];
    socket.emit("SpeechStarted", "MANUAL");

    // Stop inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const stopSpeechManual = () => {
    // endUserAnsweringTimer();
    setStartSpeakingPopover(false);
    setIsUserSpeaking(false);
    userSpeakingRef.current = false;
    console.log("Speech ended manually.");
    socket.emit("SpeechEnded");
    setIntermediateTranscript("");
  };

  const answerMayaButton = () => {
    return (
      <div>
        {isUserSpeaking ? (
          <div className="flex gap-[.85rem] items-center text-[.9rem] 2xl:text-[1rem] font-semibold">
            <Button
              id="tour-answer-button"
              type="primary"
              danger
              shape="circle"
              onClick={stopSpeechManual}
              className={`custom-disabled-button answer-circle-btn ${
                interviewMode !== "Hands-free" ? "animate-heartbeat-danger" : ""
              }`}
              icon={<Icon type="stop-button" width="18" height="18" />}
              disabled={interviewMode === "Hands-free"}
            ></Button>
            <Mic size={22} strokeWidth={2.25} />
            <div
              className={
                interviewMode === "Hands-free"
                  ? "text-gray-400 opacity-60 font-medium"
                  : "text-white"
              }
            >
              Stop answering
            </div>
          </div>
        ) : (
          <div className="flex gap-[.85rem] items-center text-[.9rem] 2xl:text-[1rem] font-semibold">
            <Button
              id="tour-answer-button"
              className={`custom-disabled-button answer-circle-btn ${
                interviewMode !== "Hands-free" ? "animate-heartbeat" : ""
              }`}
              type="primary"
              shape="circle"
              onClick={startSpeechManual}
              icon={
                <Icon
                  type="start-button"
                  width="19"
                  height="19"
                  className="ml-[2px]"
                />
              }
              disabled={interviewMode === "Hands-free"}
            ></Button>

            <Popover
              overlayInnerStyle={{
                color: "#D0D5DD",
                padding: "9px 14px",
                width: "320px",
                border: "1px solid lightgray",
                backgroundColor: "black",
              }}
              placement="top"
              open={startSpeakingPopover && interviewMode === "Manual"}
              onOpenChange={(visible) => setStartSpeakingPopover(visible)}
              content={
                <div className="text-medium text-[#D0D5DD]">
                  <div className="flex gap-1 justify-between items-start">
                    <span>
                      Are you speaking? Your mic is off. Click on the{" "}
                      <span className="font-semibold">Play</span> button and
                      start speaking.
                    </span>
                    <button
                      className="text-white hover:text-white text-[.95rem] mt-[-.15rem]"
                      onClick={() => setStartSpeakingPopover(false)}
                    >
                      ✖
                    </button>
                  </div>
                </div>
              }
            >
              <MicOff color="#ff4d4f" strokeWidth={2.1} size={22} />
            </Popover>

            <div
              className={
                interviewMode === "Hands-free"
                  ? "text-gray-400 opacity-60 font-medium"
                  : "text-white"
              }
            >
              Start answering
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {tooltipModal && (
        <InterviewTooltipModal
          tooltipModal={tooltipModal}
          setTooltipModal={setTooltipModal}
          interviewMode={interviewMode}
        />
      )}
      {voiceInactiveWarningModal && (
        <InactivityDisqualifyModal
        allowUnloadRef={allowUnloadRef}
          voiceInactiveWarningModal={voiceInactiveWarningModal}
          interviewData={interviewData}
          ip={ip}
          setLoading={setLoading}
          videoBlobQueueRef={videoBlobQueueRef}
          screenShareBlobQueueRef={screenShareBlobQueueRef}
          timeRemainingRef={timeRemainingRef}
          stopRecording={stopRecording}
          chatMessagesRef={chatMessagesRef}
          resumeInterviewHandler={resumeInterviewHandler}
        />
      )}
      <div
        id="chat"
        className="w-[95%] md:w-[80%] lg:w-[72%] xl:w-[68%] 2xl:w-[75%] pb-2 pt-[.8rem] px-5 md:px-20  2xl:max-h-[280px] max-h-[200px] overflow-y-auto my-4 sm:my-auto mx-auto text-[#F2F8FE] text-[.95rem] 2xl:text-[1.1rem] text-center flex flex-col gap-y-[.75rem]"
      >
        {chatMessages?.map((chat, index) => (
          <div
            key={index}
            className="message"
            style={{
              color: chat.role === "You" ? "#F2F8FE" : "#FEC84B",
              lineHeight: "1.45",
              fontWeight: "500",
            }}
          >
            <strong>{chat.role}:</strong> {chat.content}
          </div>
        ))}

        {/* Reference element to scroll into view */}
        <div ref={chatRef} />
      </div>
      <section className="flex flex-col gap-[.45rem] items-center py-[.2rem] w-full">
        <div className="text-[rgb(177,185,198)] text-[.88rem] italic 2xl:text-[1.02rem] font-medium text-center w-[80%] mx-auto py-1 mt-1">
          {intermediateTranscript ? (
            <>
              <span className="">You (interim transcript): </span>
              <span className=""> {intermediateTranscript}</span>
            </>
          ) : (
            <div className="invisible">Nothing</div>
          )}
        </div>

        {/* <div
          className={`${isUserSpeaking ? "visible" : "invisible"} mt-[-1.5rem]`}
        >
          <Lottie
            autoplay
            animationData={soundWaveAnimation}
            loop
            style={{ width: 35 }}
          />
        </div> */}
        {answerMayaButton()}
        {
          <div
            className={`${
              interviewMode === "Manual" ? "block" : "hidden"
            } text-[#98A2B3] font-medium text-[.83rem] 2xl:text-[.94rem] flex gap-2`}
          >
            Use Spacebar{" "}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 10C21 9.4 20.6 9 20 9C19.4 9 19 9.4 19 10V12C19 12.6 18.6 13 18 13H6C5.4 13 5 12.6 5 12V10C5 9.4 4.6 9 4 9C3.4 9 3 9.4 3 10V12C3 13.7 4.3 15 6 15H18C19.7 15 21 13.7 21 12V10Z"
                fill="#98A2B3"
              />
            </svg>
            to Start or Stop speech
          </div>
        }
        {interviewMode === "Manual" ? (
          <div
            className={`text-[#98A2B3] pt-[.2rem] font-medium text-[.83rem] 2xl:text-[.94rem] flex gap-[.45rem] justify-center items-center`}
          >
            <div>
              Want seamless automatic conversation? Try{" "}
              <span
                className="underline text-[#f2f3f6] hover:cursor-pointer"
                onClick={() => setInterviewMode("Hands-free")}
              >
                Hands-free mode
              </span>
              .
            </div>
            <div
              className="hover:cursor-pointer"
              onClick={() => setTooltipModal(true)}
            >
              <Icon
                type="help-icon"
                color="#f2f3f6"
                strokeWidth="1.5"
                width="20"
                height="20"
              />
            </div>
          </div>
        ) : (
          <div
            className={`text-[#98A2B3] pt-[.2rem] font-medium text-[.94rem] flex gap-[.4rem] justify-center items-center`}
          >
            <div>
              Facing audio issues? Try{" "}
              <span
                className="underline text-white hover:cursor-pointer"
                onClick={() => setInterviewMode("Manual")}
              >
                Manual mode
              </span>
              .
            </div>
            <div
              className="hover:cursor-pointer"
              onClick={() => setTooltipModal(true)}
            >
              <Icon
                type="help-icon"
                color="#f2f3f6"
                strokeWidth="1.5"
                width="20"
                height="20"
              />
            </div>
          </div>
        )}
      </section>
    </>
  );
}

export default TwoWayMain;
